import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  FormControlLabel,
  Grid2,
  IconButton,
  Radio,
  RadioGroup,
  useTheme,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { observer } from "mobx-react-lite";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import {
  OrderDeliveryRegion,
  OrderDeliveryType,
} from "../../../../../models/Order/OrderDelivery";
import {
  OrderShippingDetailsStore,
  ShippingCityDraft,
} from "../../../stores/OrderShippingDetailsStore";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";

const CITY_SEARCH_DEBOUNCE_MS = 400;

interface DeliveryOption<T extends string> {
  id: T;
  label: string;
  description: string;
}

const DELIVERY_REGION_OPTIONS: DeliveryOption<OrderDeliveryRegion>[] = [
  {
    id: OrderDeliveryRegion.DOMESTIC,
    label: "Domestic",
    description: "Deliveries within the country",
  },
  {
    id: OrderDeliveryRegion.INTERNATIONAL,
    label: "International",
    description: "Deliveries outside the country",
  },
  {
    id: OrderDeliveryRegion.BOTH,
    label: "Both",
    description: "Domestic and international deliveries",
  },
];

const DELIVERY_TYPE_OPTIONS: DeliveryOption<OrderDeliveryType>[] = [
  {
    id: OrderDeliveryType.REMOTE,
    label: "Remote",
    description: "Shipped to individual locations",
  },
  {
    id: OrderDeliveryType.BULK,
    label: "Bulk",
    description: "Shipped to selected cities",
  },
  {
    id: OrderDeliveryType.HYBRID,
    label: "Hybrid",
    description: "Individual locations and selected cities",
  },
];

interface OrderShippingDetailsProps {
  orderId: number;
}

interface DeliveryOptionRadioGroupProps<T extends string> {
  label: string;
  options: DeliveryOption<T>[];
  value: T | null;
  disabled: boolean;
  onChange: (value: T) => void;
}

const DeliveryOptionRadioGroup = <T extends string>({
  label,
  options,
  value,
  disabled,
  onChange,
}: DeliveryOptionRadioGroupProps<T>) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;

  return (
    <>
      <CustomTypography
        variant="heading8"
        color={colors.mediumGrayText}
        sx={{ mb: 0.5 }}
      >
        {label}
      </CustomTypography>
      <RadioGroup
        row
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value as T)}
        sx={{ gap: { xs: 1, sm: 4 } }}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.id}
            value={option.id}
            disabled={disabled}
            control={
              <Radio
                size="small"
                sx={{
                  color: colors.mediumGray,
                  "&.Mui-checked": { color: colors.purple },
                }}
              />
            }
            label={
              <Box>
                <CustomTypography variant="heading7" color={colors.darkBlueText}>
                  {option.label}
                </CustomTypography>
                <CustomTypography
                  variant="caption"
                  color={colors.mutedSlateText}
                >
                  {option.description}
                </CustomTypography>
              </Box>
            }
          />
        ))}
      </RadioGroup>
    </>
  );
};

interface CountFieldProps {
  label: string;
  value: string;
  placeholder: string;
  disabled: boolean;
  onChange: (value: string) => void;
}

const CountField = ({
  label,
  value,
  placeholder,
  disabled,
  onChange,
}: CountFieldProps) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;

  return (
    <>
      <CustomTypography
        variant="heading8"
        color={colors.mediumGrayText}
        sx={{ mb: 0.5 }}
      >
        {label}
      </CustomTypography>
      <CustomTextField
        type="number"
        width="100%"
        placeholder={placeholder}
        value={value}
        allowNegativeValue={false}
        allowDecimalValue={false}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </>
  );
};

const pluralizeCities = (count: number) =>
  `${count} ${count === 1 ? "city" : "cities"}`;

const buildLocationBreakdown = (store: OrderShippingDetailsStore): string => {
  const cityCount = pluralizeCities(store.cities.length);
  let domestic = "";
  switch (store.usesDomestic ? store.deliveryType : null) {
    case OrderDeliveryType.REMOTE:
      domestic = `${store.remoteLocationTotal} remote`;
      break;
    case OrderDeliveryType.BULK:
      domestic = `${store.cityLocationTotal} across ${cityCount}`;
      break;
    case OrderDeliveryType.HYBRID:
      domestic = `${store.remoteLocationTotal} remote + ${store.cityLocationTotal} across ${cityCount}`;
      break;
  }
  const international = `${store.internationalRemoteTotal} remote + ${store.internationalBulkTotal} bulk`;

  switch (store.deliveryRegion) {
    case OrderDeliveryRegion.DOMESTIC:
      if (store.deliveryType === OrderDeliveryType.BULK) {
        return ` across ${cityCount}`;
      }
      if (store.deliveryType === OrderDeliveryType.HYBRID) {
        return ` (${domestic})`;
      }
      return "";
    case OrderDeliveryRegion.INTERNATIONAL:
      return ` (international: ${international})`;
    case OrderDeliveryRegion.BOTH:
      return ` (domestic: ${domestic || "type not selected"} · international: ${international})`;
    default:
      return "";
  }
};

interface ShippingCityRowProps {
  store: OrderShippingDetailsStore;
  city: ShippingCityDraft;
  disabled: boolean;
}

const ShippingCityRow = observer(
  ({ store, city, disabled }: ShippingCityRowProps) => {
    const theme = useTheme();
    const colors = theme.palette.customColors;

    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          p: 1.5,
          borderRadius: "8px",
          border: `1px solid ${colors.softCardBorder}`,
          backgroundColor: colors.softCardBackground,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <CustomTypography variant="heading7" color={colors.darkBlueText}>
              {city.city_name}
            </CustomTypography>
            {city.subtitle && (
              <CustomTypography variant="caption" color={colors.mutedSlateText}>
                {city.subtitle}
              </CustomTypography>
            )}
          </Box>
          {!disabled && (
            <IconButton
              size="small"
              aria-label={`Remove ${city.city_name}`}
              onClick={() => store.removeCity(city.city_id)}
              sx={{ color: colors.errorRed, mt: -0.5, mr: -0.5 }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Box sx={{ mt: "auto" }}>
          <CustomTypography
            variant="heading9"
            color={colors.mutedSlateText}
            sx={{ mb: 0.5 }}
          >
            Locations in city
          </CustomTypography>
          <CustomTextField
            type="number"
            width="100%"
            placeholder="Locations"
            value={city.location_count}
            allowNegativeValue={false}
            allowDecimalValue={false}
            disabled={disabled}
            onChange={(event) =>
              store.setCityLocationCount(city.city_id, event.target.value)
            }
          />
        </Box>
      </Box>
    );
  },
);

const OrderShippingDetails = ({ orderId }: OrderShippingDetailsProps) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const { orderStore, userStore } = useStores();
  const [store] = useState(() => new OrderShippingDetailsStore());
  const [citySearchText, setCitySearchText] = useState("");
  const debouncedCitySearchText = useDebounce(
    citySearchText,
    CITY_SEARCH_DEBOUNCE_MS,
  );

  const canEdit = userStore.hasPermission([
    EmployeeRoles.CREATE_ORDER,
    EmployeeRoles.EDIT_ORDER,
  ]);
  const existingOrder = orderStore.existingOrder;
  const isHybrid = store.deliveryType === OrderDeliveryType.HYBRID;
  const isBothRegions = store.deliveryRegion === OrderDeliveryRegion.BOTH;
  const showsRemoteCount = store.usesRemoteCount;
  const showsCities = store.usesCities;

  useEffect(() => {
    store.setOrderId(orderId);
  }, [orderId, store]);

  useEffect(() => {
    if (existingOrder && Number(existingOrder.id) === orderId) {
      store.hydrate(existingOrder);
    }
    // Re-hydrate only when a different order loads so unsaved edits survive other order refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingOrder?.id, orderId]);

  useEffect(() => {
    if (showsCities && canEdit) {
      store.fetchCities(debouncedCitySearchText);
    }
  }, [debouncedCitySearchText, showsCities, canEdit, store]);

  const isFormDisabled = !canEdit || store.isSaving;

  const handleSave = async () => {
    try {
      const details = await store.save();
      if (details) {
        orderStore.applyOrderDeliveryDetails(details);
      }
    } catch (error) {
      console.error("Failed to sync shipping details", error);
    }
  };

  return (
    <Card
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: "12px",
        border: `1px solid ${colors.lightGray}`,
        boxShadow: `0 2px 8px ${colors.shadowLightBorder}`,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <CustomTypography variant="heading4" fontWeight={700}>
          Shipping Details
        </CustomTypography>
        <CustomTypography variant="body2" color={colors.greyTextColor}>
          How this order will be delivered: within the country, internationally,
          or both.
        </CustomTypography>
      </Box>

      <Grid2 container spacing={2}>
        <Grid2 size={12}>
          <DeliveryOptionRadioGroup
            label="Delivery region"
            options={DELIVERY_REGION_OPTIONS}
            value={store.deliveryRegion}
            disabled={isFormDisabled}
            onChange={store.setDeliveryRegion}
          />
        </Grid2>

        {store.usesDomestic && (
          <Grid2 size={12}>
            <DeliveryOptionRadioGroup
              label={isBothRegions ? "Domestic delivery type" : "Delivery type"}
              options={DELIVERY_TYPE_OPTIONS}
              value={store.deliveryType}
              disabled={isFormDisabled}
              onChange={store.setDeliveryType}
            />
          </Grid2>
        )}

        {showsRemoteCount && (
          <Grid2 size={{ xs: 12, md: 2 }}>
            <CountField
              label={isHybrid ? "Remote locations" : "Number of locations"}
              placeholder="e.g. 150"
              value={store.remoteLocationCount}
              disabled={isFormDisabled}
              onChange={store.setRemoteLocationCount}
            />
          </Grid2>
        )}

        {showsCities && (
          <>
            <Grid2 size={{ xs: 12, md: 2 }} sx={{ alignSelf: "flex-end" }}>
              <AppSearchDropdownMulti
                title="Cities"
                floatingLabel
                list={store.cityFilterOptions}
                value={store.selectedCityFilterValues}
                handleSearch={(text) => setCitySearchText(text ?? "")}
                handleSelect={store.setSelectedCities}
                disabled={isFormDisabled}
                accentColor={colors.purple}
              />
              <InlineFormError message={store.citiesError} />
            </Grid2>
            <Grid2 size={{ xs: 12, md: showsRemoteCount ? 8 : 10 }}></Grid2>
          </>
        )}

        {showsCities && (
          <Grid2 size={12}>
            {store.cities.length === 0 ? (
              <Box
                sx={{
                  py: 3,
                  textAlign: "center",
                  borderRadius: "8px",
                  border: `1px dashed ${colors.softCardBorder}`,
                }}
              >
                <CustomTypography variant="body2" color={colors.greyTextColor}>
                  No cities selected yet. Pick cities above; each starts with 1
                  location.
                </CustomTypography>
              </Box>
            ) : (
              <Grid2 container spacing={2}>
                {store.cities.map((city) => (
                  <Grid2 key={city.city_id} size={{ xs: 12, md: 6, lg: 3 }}>
                    <ShippingCityRow
                      store={store}
                      city={city}
                      disabled={isFormDisabled}
                    />
                  </Grid2>
                ))}
              </Grid2>
            )}
          </Grid2>
        )}

        {store.usesInternational && (
          <>
            <Grid2 size={12}>
              <CustomTypography
                variant="heading8"
                color={colors.mediumGrayText}
              >
                International deliveries
              </CustomTypography>
              <CustomTypography variant="caption" color={colors.mutedSlateText}>
                Enter at least one remote or bulk delivery.
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
              <CountField
                label="Remote deliveries"
                placeholder="e.g. 3"
                value={store.internationalRemoteCount}
                disabled={isFormDisabled}
                onChange={store.setInternationalRemoteCount}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
              <CountField
                label="Bulk deliveries"
                placeholder="e.g. 2"
                value={store.internationalBulkCount}
                disabled={isFormDisabled}
                onChange={store.setInternationalBulkCount}
              />
            </Grid2>
          </>
        )}
      </Grid2>

      <Box sx={{ mt: 2 }}>
        {canEdit && <InlineFormError message={store.saveError} />}
        {canEdit && store.successMessage && (
          <CustomTypography
            variant="heading8"
            color={colors.successGreen}
            sx={{ mb: 1 }}
          >
            {store.successMessage}
          </CustomTypography>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {store.deliveryRegion ? (
            <CustomTypography variant="body1" color={colors.navyText}>
              Total locations: <strong>{store.totalLocationCount}</strong>
              {buildLocationBreakdown(store)}
            </CustomTypography>
          ) : (
            <CustomTypography variant="body2" color={colors.greyTextColor}>
              Delivery details are not set for this order yet.
            </CustomTypography>
          )}
          {canEdit && (
            <AppButton
              variant="primary"
              label={store.isSaving ? "Saving..." : "Save Shipping Details"}
              onClick={handleSave}
              disabled={store.isSaving || !store.deliveryRegion}
              customStyles={{ minWidth: "120px", flexShrink: 0 }}
            />
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default observer(OrderShippingDetails);
